import type { AircraftState, EpochMs, Freshness, LatLon } from './types.ts';
import { angleDiffDeg, bearingDeg, destinationPoint, distanceM, M_PER_FT, MPS_PER_KT } from './geo.ts';
import { ESTIMATE_ERROR_FRACTION, MAX_EXTRAPOLATE_SEC } from './world.ts';
import { COVERAGE_PREDICT_SEC, COVERAGE_STEP_SEC } from './coverage.ts';

/*
 * Predicted track (3D): the subject's constant-velocity future as honest samples with growing
 * uncertainty. Everything here is provenance class E (aviation data spec §2): drawn hollow/dashed,
 * never as a bare number, always with its cone (σ).
 *
 * Tier limits (aviation data spec §11 "Prediction tiers"): dead reckoning from the filter state is
 * "good for 5–15 s; the σ cone grows about linearly with time"; beyond ~15 s only a plan-constrained
 * predictor is "best". We have no plan here, so past 15 s the line is still drawn — the map already
 * leads 120 s and crews need it — but as 'projected' (dashed, cone mandatory), never as the solid
 * estimate. The spec has no hard "never extrapolate past N s" rule (world.ts holds a 60 s estimate; the
 * spec's §2 staleness table decides when a fix is stale): 'suppressed' follows freshness, i.e. a stale or
 * lost aircraft produces no forecast (field feedback 2026-10-14: "Stale aircraft suppress forecasts").
 */

/** Spec §11 tier 1: dead reckoning is good for 5–15 s. Seconds since the fix up to which a sample is 'solid'. */
export const SOLID_PROJECTION_SEC = 15;
/** Look-ahead of the drawn line (s); the same 2 min as the coverage "next in frame" horizon. */
export const PROJECTION_HORIZON_SEC = COVERAGE_PREDICT_SEC;
/** Sample spacing (s). */
export const PROJECTION_STEP_SEC = COVERAGE_STEP_SEC;
/** Vertical σ is this fraction of |vs|·t … */
export const VERTICAL_SIGMA_FRACTION = 0.1;
/** … never below this (m): barometric/geometric altitude and vs noise alone are about this large. */
export const VERTICAL_SIGMA_FLOOR_M = 30;
/** Fraction of the lateral deviation a continued (or abandoned) turn would cause that is added to σ cross-track. */
export const TURN_SIGMA_FRACTION = 0.5;
/** Window (s) of recent track used to estimate the turn rate. */
export const TURN_HISTORY_SEC = 30;
/** Ignore track segments shorter than this (m): GPS jitter, not heading. */
const TURN_MIN_SEGMENT_M = 20;

/** 'solid' = inside the dead-reckoning limit; 'projected' = beyond it (class E, dashed, cone); 'suppressed' = no forecast. */
export type TrackTier = 'solid' | 'projected' | 'suppressed';

export interface TrackSample {
  /** Seconds after the state's time. */
  tSec: number;
  lat: number;
  lon: number;
  /** MSL metres; null when the aircraft's altitude (or its reference) is unknown. */
  altM: number | null;
  /** 1σ horizontal position uncertainty (m). */
  sigmaHM: number;
  /** 1σ vertical uncertainty (m); null with altM. */
  sigmaVM: number | null;
  tier: Exclude<TrackTier, 'suppressed'>;
}

export interface HistoryPoint extends LatLon {
  t: EpochMs;
}

export interface ProjectOptions {
  /** Look-ahead (s); default and maximum PROJECTION_HORIZON_SEC. */
  horizonSec?: number;
  /** Sample spacing (s); default PROJECTION_STEP_SEC. */
  stepSec?: number;
  /** Recent positions, oldest first, for the turn rate; default the state's own trail. */
  history?: readonly HistoryPoint[];
}

/**
 * Confidence tier of a forecast `aheadSec` past a state that is `ageSec` old. Stale/lost/no data →
 * 'suppressed'; within SOLID_PROJECTION_SEC of the last fix → 'solid'; beyond (up to the horizon) →
 * 'projected'. A low-integrity position is never 'solid'.
 */
export function confidenceTier(ageSec: number | null | undefined, freshness: Freshness, aheadSec = 0, opts: { lowIntegrity?: boolean } = {}): TrackTier {
  if (ageSec == null || !Number.isFinite(ageSec) || !Number.isFinite(aheadSec) || aheadSec < 0) return 'suppressed';
  if (freshness !== 'live' && freshness !== 'aging') return 'suppressed';
  if (aheadSec > PROJECTION_HORIZON_SEC) return 'suppressed';
  if (!opts.lowIntegrity && Math.max(0, ageSec) + aheadSec <= SOLID_PROJECTION_SEC) return 'solid';
  return 'projected';
}

/** Turn rate (°/s, signed, + = right) over the last `windowSec` of `history`; 0 when there is not enough movement to tell. */
export function recentTurnRateDegS(history: readonly HistoryPoint[] | undefined, windowSec = TURN_HISTORY_SEC): number {
  if (!history || history.length < 3) return 0;
  const last = history[history.length - 1]!;
  const from = last.t - windowSec * 1000;
  const segs: Array<{ brg: number; t: number }> = [];
  let prev: HistoryPoint | null = null;
  for (const p of history) {
    if (p.t < from || !Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
    if (prev && p.t > prev.t && distanceM(prev, p) >= TURN_MIN_SEGMENT_M) {
      segs.push({ brg: bearingDeg(prev, p), t: (prev.t + p.t) / 2 });
      prev = p;
    } else if (!prev) prev = p;
  }
  if (segs.length < 2) return 0;
  const dt = (segs[segs.length - 1]!.t - segs[0]!.t) / 1000;
  if (!(dt > 0)) return 0;
  // Sum the consecutive segment bearing changes: first-vs-last wraps at ±180° (a fast orbit would read ≈ 0).
  let total = 0;
  for (let i = 1; i < segs.length; i++) total += angleDiffDeg(segs[i - 1]!.brg, segs[i]!.brg);
  return total / dt;
}

/**
 * 1σ uncertainty `sinceFixSec` after the newest real fix. Horizontal = hypot(reported accuracy,
 * ESTIMATE_ERROR_FRACTION × distance flown) — the same law as world.ts `estimateM` — widened
 * cross-track when the aircraft is turning (a turn that continues or stops moves it ≈ ½·v·ω·t² sideways).
 * Vertical ≈ VERTICAL_SIGMA_FRACTION of |vs|·t with a VERTICAL_SIGMA_FLOOR_M floor. Null without a ground speed.
 */
export function uncertaintyAt(
  s: Pick<AircraftState, 'gsKt' | 'vsFpm' | 'accuracyM'>,
  sinceFixSec: number,
  turnRateDegS = 0,
): { sigmaHM: number; sigmaVM: number } | null {
  if (s.gsKt == null || !Number.isFinite(s.gsKt) || !Number.isFinite(sinceFixSec)) return null;
  const t = Math.max(0, sinceFixSec);
  const v = Math.max(0, s.gsKt) * MPS_PER_KT;
  const flown = v * t;
  const w = Math.abs(turnRateDegS) * (Math.PI / 180);
  const lateral = Math.min(flown, TURN_SIGMA_FRACTION * 0.5 * v * w * t * t);
  const acc = s.accuracyM != null && Number.isFinite(s.accuracyM) ? Math.max(0, s.accuracyM) : 0;
  const sigmaHM = Math.hypot(acc, flown * ESTIMATE_ERROR_FRACTION, lateral);
  const vs = Math.abs(s.vsFpm ?? 0) * M_PER_FT / 60;
  const sigmaVM = Math.max(VERTICAL_SIGMA_FLOOR_M, VERTICAL_SIGMA_FRACTION * vs * t);
  return { sigmaHM, sigmaVM };
}

/** True when the state has what a forecast needs: a position, a ground speed, and a track unless it is stationary. */
function projectable(s: AircraftState): boolean {
  if (!Number.isFinite(s.lat) || Math.abs(s.lat) > 90 || !Number.isFinite(s.lon) || Math.abs(s.lon) > 180) return false;
  if (s.gsKt == null || !Number.isFinite(s.gsKt) || s.gsKt < 0) return false;
  return s.gsKt < 1 || (s.trackDeg != null && Number.isFinite(s.trackDeg));
}

/**
 * Where to dead-reckon from. A dead-reckoned state sits `ageSec` along its track from the real fix
 * (world.ts); great-circle legs do not compose exactly, so when the fix is still in the trail we
 * measure from it, which reproduces world.ts at the same instant to well under a metre.
 */
function originOf(s: AircraftState): { pos: LatLon; offsetSec: number } {
  if (s.quality === 'estimated' && s.ageSec > 0 && s.ageSec <= MAX_EXTRAPOLATE_SEC) {
    const fix = s.trail.find((p) => p.t === s.lastObservedT);
    if (fix) return { pos: fix, offsetSec: s.ageSec };
  }
  return { pos: s, offsetSec: 0 };
}

/** Altitude reference we can trust as MSL: absent means MSL; unconverted PA/HAE is withheld. */
function altMslM(s: AircraftState): number | null {
  if (s.altFt == null || !Number.isFinite(s.altFt)) return null;
  if (s.altRef != null && s.altRef !== 'MSL') return null;
  return s.altFt * M_PER_FT;
}

/** Constant-velocity position `tSec` after the state's time (no tier or horizon check); null if not projectable. */
export function projectPoint(s: AircraftState, tSec: number): { lat: number; lon: number; altM: number | null } | null {
  if (!projectable(s) || !Number.isFinite(tSec) || tSec < 0) return null;
  const { pos, offsetSec } = originOf(s);
  const flownM = s.gsKt! * MPS_PER_KT * (offsetSec + tSec);
  const p = flownM > 0 && s.trackDeg != null ? destinationPoint(pos, s.trackDeg, flownM) : { lat: pos.lat, lon: pos.lon };
  let altM = altMslM(s);
  if (altM != null) {
    altM += ((s.vsFpm ?? 0) * M_PER_FT * tSec) / 60;
    // never below the ground we know of (held at the aircraft's current ground elevation)
    if (s.groundElevM != null && Number.isFinite(s.groundElevM)) altM = Math.max(altM, s.groundElevM);
  }
  return { lat: p.lat, lon: p.lon, altM };
}

/**
 * The forecast line from `s`: constant velocity along the track, samples every `stepSec` from 0 to the
 * horizon. Empty (never invented) when the state is stale/lost or lacks position, speed or track.
 */
export function projectTrack(s: AircraftState, opts: ProjectOptions = {}): TrackSample[] {
  if (!projectable(s)) return [];
  if (confidenceTier(s.ageSec, s.freshness, 0, { lowIntegrity: s.lowIntegrity }) === 'suppressed') return [];
  const horizon = Math.min(PROJECTION_HORIZON_SEC, opts.horizonSec ?? PROJECTION_HORIZON_SEC);
  const step = opts.stepSec != null && opts.stepSec > 0 ? opts.stepSec : PROJECTION_STEP_SEC;
  const turn = recentTurnRateDegS(opts.history ?? s.trail);
  const age = Math.max(0, s.ageSec);
  const out: TrackSample[] = [];
  for (let i = 0; i * step <= horizon + 1e-9; i++) {
    const tSec = i * step;
    const tier = confidenceTier(s.ageSec, s.freshness, tSec, { lowIntegrity: s.lowIntegrity });
    const p = projectPoint(s, tSec);
    const u = uncertaintyAt(s, age + tSec, turn);
    if (tier === 'suppressed' || !p || !u) break;
    out.push({ tSec, lat: p.lat, lon: p.lon, altM: p.altM, sigmaHM: u.sigmaHM, sigmaVM: p.altM != null ? u.sigmaVM : null, tier });
  }
  return out;
}
